import { Link } from 'react-router-dom'
import { ChevronRight, GitBranch } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { formatDateTime } from '@/lib/format'
import type { LinkedAlert, LinkedCase, LinkedDecision } from '../types'

interface Props {
  alerts: LinkedAlert[]
  cases: LinkedCase[]
  decisions: LinkedDecision[]
}

const ALERT_STATUS_VARIANT: Record<
  LinkedAlert['status'],
  React.ComponentProps<typeof Badge>['variant']
> = {
  open: 'pending',
  in_review: 'info',
  closed: 'cancelled',
  escalated: 'critical',
  closed_fraud: 'failed',
  closed_false_positive: 'success',
  closed_duplicate: 'cancelled',
}

const CASE_STATUS_VARIANT: Record<
  LinkedCase['status'],
  React.ComponentProps<typeof Badge>['variant']
> = {
  open: 'pending',
  in_review: 'info',
  closed: 'cancelled',
  fraud_confirmed: 'critical',
  false_positive: 'success',
}

const DECISION_VARIANT: Record<
  LinkedDecision['action'],
  React.ComponentProps<typeof Badge>['variant']
> = {
  allow: 'success',
  review: 'pending',
  step_up: 'medium',
  block: 'critical',
}

/**
 * AF-125 — surfaces every entity tied to this transaction so analysts
 * can pivot in one click: alerts → AF-126 detail (and their backing
 * scenario via the chip), cases → AF-127, decisions render as an
 * outcome row (a decision aggregates many signals, so there's no
 * single scenario to pivot to).
 */
export function LinksPanel({ alerts, cases, decisions }: Props) {
  if (alerts.length === 0 && cases.length === 0 && decisions.length === 0) {
    return (
      <p className="text-sm text-muted-fg">
        No alerts, cases, or decisions are linked to this transaction.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {alerts.length > 0 && (
        <Group title="Alerts">
          {alerts.map((a) => {
            const primary = a.scenario_name?.trim() || a.alert_number
            const secondaryParts = [a.alert_number]
            if (a.scenario_code) secondaryParts.push(a.scenario_code)
            secondaryParts.push(`score ${a.score}`)
            return (
              <Row
                key={a.id}
                to={`/alerts/${a.id}`}
                primary={primary}
                secondary={secondaryParts.join(' · ')}
                right={
                  <div className="flex items-center gap-1.5">
                    {a.scenario_code ? (
                      <ScenarioChip code={a.scenario_code} />
                    ) : null}
                    <Badge variant={ALERT_STATUS_VARIANT[a.status]}>
                      {a.status.replaceAll('_', ' ')}
                    </Badge>
                  </div>
                }
              />
            )
          })}
        </Group>
      )}

      {cases.length > 0 && (
        <Group title="Cases">
          {cases.map((c) => (
            <Row
              key={c.id}
              to={`/cases/${c.id}`}
              primary={c.case_number}
              right={
                <Badge variant={CASE_STATUS_VARIANT[c.status]}>
                  {c.status.replaceAll('_', ' ')}
                </Badge>
              }
            />
          ))}
        </Group>
      )}

      {decisions.length > 0 && (
        <Group title="Decisions">
          {decisions.map((d) => {
            const secondaryParts: string[] = []
            if (d.primary_reason) secondaryParts.push(d.primary_reason)
            if (d.risk_score !== undefined && d.risk_score !== null) {
              secondaryParts.push(`risk ${d.risk_score}`)
            }
            secondaryParts.push(formatDateTime(d.scored_at))
            return (
              <NonLinkRow
                key={d.id}
                primary={`Decision · ${d.action.replaceAll('_', ' ')}`}
                secondary={secondaryParts.join(' · ')}
                right={<Badge variant={DECISION_VARIANT[d.action]}>{d.action.replaceAll('_', ' ')}</Badge>}
              />
            )
          })}
        </Group>
      )}
    </div>
  )
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-fg">
        {title}
      </h3>
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  )
}

function Row({
  to,
  primary,
  secondary,
  right,
}: {
  to: string
  primary: string
  secondary?: string
  right?: React.ReactNode
}) {
  // The row's text is the primary link target; `right` can carry its
  // own interactive widgets (chips that are themselves links, badges,
  // etc.). Wrapping the whole row in a single <Link> nests those
  // descendants inside an anchor, which React 19 surfaces as
  // "in HTML, <a> cannot be a descendant of <a>". The layout is now
  // sibling-flat: a positioned Link spans the row for click affordance
  // (so the entire row is still a click target), and `right` renders
  // above it via `relative z-10` so the chips remain clickable.
  return (
    <li className="relative flex items-center justify-between gap-3 rounded border border-transparent px-2 py-2 text-sm transition-colors hover:border-border hover:bg-muted">
      <Link
        to={to}
        className="absolute inset-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={primary}
      />
      <div className="relative min-w-0 flex-1 pointer-events-none">
        <div className="truncate font-medium">{primary}</div>
        {secondary && (
          <div className="truncate text-xs text-muted-fg">{secondary}</div>
        )}
      </div>
      {right && <div className="relative z-10">{right}</div>}
      <ChevronRight
        size={14}
        className="relative shrink-0 text-muted-fg pointer-events-none"
        aria-hidden
      />
    </li>
  )
}

/**
 * Identical visual to Row but without a wrapping Link, for rows that
 * carry information but have no canonical target. Decisions in
 * particular: there's no decision-detail page, and the historical
 * /admin/scenarios link was bogus.
 */
function NonLinkRow({
  primary,
  secondary,
  right,
}: {
  primary: string
  secondary?: string
  right?: React.ReactNode
}) {
  return (
    <li>
      <div className="flex items-center justify-between gap-3 rounded border border-transparent px-2 py-2 text-sm">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{primary}</div>
          {secondary && (
            <div className="truncate text-xs text-muted-fg">{secondary}</div>
          )}
        </div>
        {right}
      </div>
    </li>
  )
}

/**
 * Small clickable chip that opens the scenario admin page for the
 * given code. Stops event propagation so clicking the chip doesn't
 * also follow the parent row's link to the alert detail.
 */
function ScenarioChip({ code }: { code: string }) {
  return (
    <Link
      to={`/admin/scenarios/${code}`}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[0.65rem] text-muted-fg hover:border-primary/40 hover:text-primary"
      aria-label={`Open scenario ${code}`}
    >
      <GitBranch size={10} aria-hidden />
      {code}
    </Link>
  )
}
